import { STORAGE_KEYS } from "./constants";

// THE AGENT RUN THIS TAB IS WATCHING, remembered across a remount.
//
// A run announces where to watch it exactly once, on an enriched `typing` activity —
// the one activity type the backend forwards to the browser WITHOUT storing it in
// history (see web_app.send_agent_progress). The frontend held that URL in React state
// and nowhere else, so navigating away and back, or reloading, left the browser with no
// handle to a run that was still working: the progress card disappeared for the rest of
// the run and the working panel never opened when the answer landed. The run itself was
// unaffected and the answer still arrived — but for two minutes the app looked idle
// while it worked.
//
// The backend already replays a run's whole progress channel before tailing it, so a
// remembered URL is all a remount needs to rebuild the card and carry on live.
//
// sessionStorage, not localStorage, ON PURPOSE: this is "what THIS tab was looking at".
// Two tabs on two threads must not inherit each other's run, which is the same reason
// the active conversation is pinned per tab (STORAGE_KEYS.TAB_CONVERSATION).

// How long a remembered run may still be reattached to. Past this we assume it ended
// while the tab was away, and reconnecting would only spin: the channel file may be
// gone (the stream endpoint 404s), and a run that outlives this was not going to be
// watched to the end anyway. The answer is in history either way.
export const RUN_MEMORY_MS = 30 * 60 * 1000;

// Every access is wrapped: sessionStorage throws outright in some privacy modes, and
// losing the progress display must never cost anyone the page.

export function rememberAgentRun({ conversationId, streamUrl, workUrl, question }) {
  if (!conversationId || !streamUrl) return;
  try {
    sessionStorage.setItem(STORAGE_KEYS.ACTIVE_AGENT_RUN, JSON.stringify({
      conversationId,
      streamUrl,
      workUrl: workUrl || "",
      // The question heads the working panel. Remembered with the run so a remount
      // reopens it under its own title instead of a bare "The working".
      question: question || "",
      startedAt: Date.now(),
    }));
  } catch (e) { /* storage unavailable — the run just won't survive a remount */ }
}

/** The run to reattach to for `conversationId`, or null. Clears what it rejects. */
export function recallAgentRun(conversationId, now = Date.now()) {
  if (!conversationId) return null;
  let stored = null;
  try {
    stored = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.ACTIVE_AGENT_RUN));
  } catch (e) {
    // Unreadable or not JSON. Drop it rather than tripping over it on every mount.
    forgetAgentRun();
    return null;
  }
  if (!stored || !stored.streamUrl || !stored.conversationId) return null;
  // A DIFFERENT THREAD'S RUN IS NOT THIS THREAD'S. Reattaching it would put another
  // question's progress under this one — worse than showing nothing.
  if (stored.conversationId !== conversationId) return null;
  if (!stored.startedAt || now - stored.startedAt > RUN_MEMORY_MS) {
    forgetAgentRun();
    return null;
  }
  return stored;
}

export function forgetAgentRun() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_AGENT_RUN);
  } catch (e) { /* nothing to clear */ }
}


// THE WORKING PANEL THIS TAB HAD OPEN, remembered across a reload.
//
// Reported 2026-09-28: with an answer's working open beside the chat, a reload brought
// the chat back and the panel gone — the split lived only in React state. The panel is
// part of what the reader was looking at, so it is remembered per tab like the run, and
// reopened on the same finished journal once history has loaded the answer it belongs to.
export function rememberWorkingPanel({ conversationId, workUrl, title }) {
  if (!conversationId || !workUrl) return;
  try {
    sessionStorage.setItem(STORAGE_KEYS.WORKING_PANEL, JSON.stringify({
      conversationId, workUrl, title: title || "",
    }));
  } catch (e) { /* storage unavailable — the panel just won't survive a reload */ }
}

/** The panel to reopen for `conversationId`, or null. */
export function recallWorkingPanel(conversationId) {
  if (!conversationId) return null;
  let stored = null;
  try {
    stored = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.WORKING_PANEL));
  } catch (e) {
    forgetWorkingPanel();
    return null;
  }
  if (!stored || !stored.workUrl || stored.conversationId !== conversationId) return null;
  return stored;
}

export function forgetWorkingPanel() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.WORKING_PANEL);
  } catch (e) { /* nothing to clear */ }
}
